import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import type { MortuarySettingsView, MortuarySlotView } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  Dialog,
  DialogActions,
  LoadingBlock,
  Notice,
  Row,
  Section,
  Select,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TextArea,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import {
  createMortuarySlot,
  mortuarySettingsQuery,
  updateMortuarySettings,
  updateMortuarySlot,
} from '../api/mortuary.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { TableScroll } from '../layout/screen.tsx';
import { SettingsPage } from '../routes/settings-nav.tsx';
import { toasts } from '../toasts.ts';
import styles from '../vital-events/vital-events.module.css';
import { errorText } from '../vital-events/words.ts';

// Settings > Mortuary (docs/plans/phase2-mortuary-and-notifications.md §6): what a day of
// storage is charged as, and the cold-room slots. Slots are retired, never deleted.

const SLOT_STATUS_TAGS: Record<
  MortuarySlotView['status'],
  [string, 'active' | 'attention' | 'neutral']
> = {
  active: ['In service', 'active'],
  out_of_service: ['Out of service', 'attention'],
  retired: ['Retired', 'neutral'],
};

function useSave() {
  const queryClient = useQueryClient();
  return (view: MortuarySettingsView) => {
    queryClient.setQueryData(mortuarySettingsQuery.queryKey, view);
    void queryClient.invalidateQueries({ queryKey: ['mortuary', 'board'] });
  };
}

function StorageCharge({ view }: { view: MortuarySettingsView }) {
  const save = useSave();
  const online = useOnline();
  const [itemId, setItemId] = useState<string | null>(view.storageItem?.id ?? null);
  const update = useMutation({
    mutationFn: () => updateMortuarySettings(itemId ?? ''),
    onSuccess: (next) => {
      save(next);
      toasts.show({ title: 'Storage charge saved' });
    },
  });
  return (
    <Section
      title="Daily storage charge"
      description="Each day a body is in storage is charged as this service, priced for the mortuary visit's payer."
    >
      {view.storageItemOptions.length === 0 ? (
        <Notice tone="info">
          No active service in the Mortuary category yet. Add one in Settings &gt; Services and
          prices first.
        </Notice>
      ) : (
        <div className={styles.actions}>
          <Select
            label="Charged as"
            placeholder="Choose one"
            options={view.storageItemOptions.map((o) => ({
              id: o.id,
              label: `${o.code} ${o.name}`,
            }))}
            value={itemId}
            onChange={(v) => {
              setItemId(typeof v === 'string' ? v : null);
            }}
          />
          <Button
            variant="primary"
            isDisabled={!online || itemId === null || itemId === view.storageItem?.id}
            isPending={update.isPending}
            pendingLabel="Saving…"
            onPress={() => {
              update.mutate();
            }}
          >
            Save storage charge
          </Button>
        </div>
      )}
      {update.error === null ? null : (
        <Notice tone="error">{errorText(update.error, 'The charge was not saved.')}</Notice>
      )}
    </Section>
  );
}

function SlotDialog({
  slot,
  onClose,
}: {
  /** Null to add a new slot. */
  slot: MortuarySlotView | null;
  onClose: () => void;
}) {
  const save = useSave();
  const [coldRoom, setColdRoom] = useState(slot?.coldRoom ?? 'Cold room 1');
  const [label, setLabel] = useState(slot?.label ?? '');
  const [status, setStatus] = useState<MortuarySlotView['status']>(slot?.status ?? 'active');
  const [reason, setReason] = useState('');
  const statusChanged = slot !== null && status !== slot.status;
  const mutation = useMutation({
    mutationFn: () =>
      slot === null
        ? createMortuarySlot({ coldRoom: coldRoom.trim(), label: label.trim() })
        : updateMortuarySlot(slot.id, {
            coldRoom: coldRoom.trim(),
            label: label.trim(),
            ...(statusChanged
              ? { status, statusReason: status === 'active' ? null : reason.trim() }
              : {}),
          }),
    onSuccess: (next) => {
      save(next);
      toasts.show({ title: slot === null ? 'Slot added' : 'Slot saved' });
      onClose();
    },
  });
  const ready =
    coldRoom.trim() !== '' &&
    label.trim() !== '' &&
    (!statusChanged || status === 'active' || reason.trim().length >= 5);
  return (
    <Dialog
      title={slot === null ? 'Add slot' : `Slot ${slot.label}`}
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <TextField label="Cold room" value={coldRoom} onChange={setColdRoom} />
      <TextField label="Slot label" description="Like A1." value={label} onChange={setLabel} />
      {slot === null || slot.status === 'retired' ? null : (
        <Select
          label="State"
          options={[
            { id: 'active', label: 'In service' },
            { id: 'out_of_service', label: 'Out of service' },
            { id: 'retired', label: 'Retired' },
          ]}
          value={status}
          onChange={(v) => {
            if (typeof v === 'string') setStatus(v as MortuarySlotView['status']);
          }}
        />
      )}
      {statusChanged && status !== 'active' ? (
        <TextArea label="Reason" value={reason} onChange={setReason} rows={2} />
      ) : null}
      {mutation.error === null ? null : (
        <Notice tone="error">{errorText(mutation.error, 'The slot was not saved.')}</Notice>
      )}
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isDisabled={!ready}
          isPending={mutation.isPending}
          pendingLabel="Saving…"
          onPress={() => {
            mutation.mutate();
          }}
        >
          {slot === null ? 'Add slot' : 'Save slot'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function MortuarySettingsPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const settings = useQuery(mortuarySettingsQuery);
  const online = useOnline();
  const [editing, setEditing] = useState<MortuarySlotView | 'new' | null>(null);
  if (session === null || !can(session, 'mortuary.settings.manage')) {
    return (
      <SettingsPage title="Mortuary">
        <Notice tone="info">You don&apos;t have permission to set up the mortuary.</Notice>
      </SettingsPage>
    );
  }
  return (
    <SettingsPage title="Mortuary">
      {settings.isPending ? <LoadingBlock label="Loading the mortuary settings" rows={3} /> : null}
      {settings.isError ? (
        <Notice
          tone="error"
          action={<Button onPress={() => void settings.refetch()}>Try again</Button>}
        >
          {errorText(settings.error, 'The settings could not be loaded. Try again.')}
        </Notice>
      ) : null}
      {settings.isSuccess ? (
        <>
          <StorageCharge view={settings.data} />
          <Section
            title="Cold-room slots"
            actions={
              <Button
                isDisabled={!online}
                onPress={() => {
                  setEditing('new');
                }}
              >
                Add slot
              </Button>
            }
          >
            {settings.data.slots.length === 0 ? (
              <p className={styles.meta}>
                No slots yet. Add one for each place a body can be stored.
              </p>
            ) : (
              <TableScroll>
                <Table aria-label="Cold-room slots" flat>
                  <TableHeader>
                    <Column isRowHeader>Slot</Column>
                    <Column>Cold room</Column>
                    <Column>State</Column>
                    <Column>Action</Column>
                  </TableHeader>
                  <TableBody items={settings.data.slots}>
                    {(s) => (
                      <Row id={s.id}>
                        <Cell>{s.label}</Cell>
                        <Cell>{s.coldRoom}</Cell>
                        <Cell>
                          <Tag state={SLOT_STATUS_TAGS[s.status][1]}>
                            {s.occupied ? 'Occupied' : SLOT_STATUS_TAGS[s.status][0]}
                          </Tag>
                        </Cell>
                        <Cell>
                          {s.status === 'retired' ? (
                            <span className={styles.meta}>{s.statusReason ?? ''}</span>
                          ) : (
                            <Button
                              dense
                              variant="quiet"
                              isDisabled={!online}
                              onPress={() => {
                                setEditing(s);
                              }}
                            >
                              Change
                            </Button>
                          )}
                        </Cell>
                      </Row>
                    )}
                  </TableBody>
                </Table>
              </TableScroll>
            )}
          </Section>
        </>
      ) : null}
      {editing === null ? null : (
        <SlotDialog
          slot={editing === 'new' ? null : editing}
          onClose={() => {
            setEditing(null);
          }}
        />
      )}
    </SettingsPage>
  );
}
